/*
 * Free Shipping Progress Bar
 * ---------------------------------------------------------------------------
 * - Shopify Markets aware: thresholds keyed by currency ISO code.
 * - Reads the live cart from /cart.js so it always reflects the market
 *   currency, discounts, quantity changes, add/remove, etc.
 * - No hardcoded currency symbols: amounts are formatted with
 *   Intl.NumberFormat using the cart's active (presentment) currency.
 * - Re-renders on cart change via fetch + XHR interception and the common
 *   theme events (this theme dispatches `cart:build`). Works on the cart page
 *   and on the cloned copy the theme injects into the cart drawer.
 */
(function () {
  'use strict';

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var instances = [];
  var cartCache = null;
  var cartFetchInFlight = null;
  var refreshScheduled = false;

  /* -- Money formatting (currency-aware, no hardcoded symbols) ------------- */

  var ZERO_DECIMAL = { JPY: 1, KRW: 1, VND: 1, CLP: 1, ISK: 1, HUF: 1, XOF: 1, XAF: 1, BIF: 1, DJF: 1, GNF: 1, KMF: 1, RWF: 1, PYG: 1, UGX: 1, VUV: 1, XPF: 1 };

  function decimalsFor(currency) {
    return ZERO_DECIMAL[currency] ? 0 : 2;
  }

  function toMinorUnits(amountMajor, currency) {
    return Math.round(Number(amountMajor) * Math.pow(10, decimalsFor(currency)));
  }

  function formatMoney(amountMinor, currency, locale) {
    var decimals = decimalsFor(currency);
    var amountMajor = amountMinor / Math.pow(10, decimals);
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency',
        currency: currency
      }).format(amountMajor);
    } catch (e) {
      return amountMajor.toFixed(decimals) + ' ' + currency;
    }
  }

  /* -- Rendering ----------------------------------------------------------- */

  function render(instance, cart) {
    var cfg = instance.config;
    var el = instance.el;

    var currency = (cart && cart.currency) || cfg.activeCurrency;
    var locale = cfg.locale;
    var thresholdMajor = cfg.thresholds[currency];

    // No threshold for this market/currency -> hide.
    if (thresholdMajor === undefined || thresholdMajor === null || thresholdMajor === 0 || thresholdMajor === '') {
      el.hidden = true;
      return;
    }

    var thresholdMinor = toMinorUnits(thresholdMajor, currency);
    var total = cart ? cart.total_price : 0;
    var itemCount = cart ? cart.item_count : 0;

    if (cfg.settings.hideWhenEmpty && itemCount === 0) {
      el.hidden = true;
      return;
    }

    var remaining = Math.max(thresholdMinor - total, 0);
    var unlocked = total >= thresholdMinor;
    var percent = thresholdMinor > 0
      ? Math.min(Math.round((total / thresholdMinor) * 100), 100)
      : 100;

    if (unlocked && !cfg.settings.showBarOnSuccess) {
      el.hidden = true;
      return;
    }
    el.hidden = false;

    var messageText;
    if (unlocked) {
      messageText = cfg.messages.success;
    } else {
      messageText = cfg.messages.progress.replace(/\[amount\]/g, formatMoney(remaining, currency, locale));
    }

    var textEl = el.querySelector('[data-fsb-message-text]');
    if (textEl) textEl.textContent = messageText;

    var fill = el.querySelector('[data-fsb-fill]');
    if (fill) fill.style.width = percent + '%';

    var track = el.querySelector('[data-fsb-track]');
    if (track) track.setAttribute('aria-valuenow', String(percent));

    el.classList.toggle('is-unlocked', unlocked);
  }

  /* -- Cart fetching ------------------------------------------------------- */

  function getCart(forceRefresh) {
    if (!forceRefresh && cartCache) return Promise.resolve(cartCache);
    if (cartFetchInFlight) return cartFetchInFlight;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root.replace(/\/$/, '') + '/cart.js';

    cartFetchInFlight = fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) { cartCache = cart; cartFetchInFlight = null; return cart; })
      .catch(function () { cartFetchInFlight = null; return null; });

    return cartFetchInFlight;
  }

  function refreshAll(forceRefresh) {
    getCart(forceRefresh).then(function (cart) {
      // Re-scan so bars injected after load (e.g. the drawer copy) are picked up.
      initAll();
      instances.forEach(function (instance) {
        if (document.contains(instance.el)) render(instance, cart);
      });
    });
  }

  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    setTimeout(function () { refreshScheduled = false; refreshAll(true); }, 120);
  }

  /* -- Cart change detection ----------------------------------------------- */

  function installInterceptors() {
    if (window.fetch && !window.fetch.__fsbPatched) {
      var origFetch = window.fetch;
      var patched = function (input) {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var isMutation = CART_MUTATION_RE.test(url);
        return origFetch.apply(this, arguments).then(function (response) {
          if (isMutation) scheduleRefresh();
          return response;
        });
      };
      patched.__fsbPatched = true;
      window.fetch = patched;
    }

    if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__fsbPatched) {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__fsbCartMutation = CART_MUTATION_RE.test(url || '');
        return origOpen.apply(this, arguments);
      };
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        if (this.__fsbCartMutation) {
          this.addEventListener('load', scheduleRefresh);
        }
        return origSend.apply(this, arguments);
      };
      XMLHttpRequest.prototype.__fsbPatched = true;
    }

    ['cart:build', 'cart:updated', 'cart:refresh', 'cart:change', 'ajaxCart.afterCartLoad', 'cartUpdated']
      .forEach(function (evt) { document.addEventListener(evt, scheduleRefresh); });

    document.addEventListener('shopify:section:load', scheduleRefresh);
  }

  /* -- Init ---------------------------------------------------------------- */

  function parseConfig(el) {
    var script = el.querySelector('[data-fsb-config]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-free-shipping-bar]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.__fsbInit) return;
      var config = parseConfig(el);
      if (!config) return;
      el.__fsbInit = true;
      instances.push({ el: el, config: config });
    });
  }

  function boot() {
    installInterceptors();
    initAll();
    refreshAll(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.FreeShippingBar = { refresh: function () { refreshAll(true); }, init: initAll };
})();
